// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { PackageService } from '@way/theme-host/services/package.service';
import { Package } from '@way/theme-host/shared/types';
import { NotificationService } from '@way/theme-host/services/notification.service';
import {
  DarkPageHeaderComponent as PageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { DarkPageFooterComponent as PageFooterComponent } from '../shared/footer/footer.component';
import {
  SharedPackageSelectionModalComponent,
  SelectedPackage,
} from '@way/website-themes/themes/shared/package-selection-modal/package-selection-modal.component';
import { PACKAGE_ICON_FALLBACK } from '@way/theme-host/shared/constants/website-builder-assets';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

// Import WebsiteElement type from home-page-preview
export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'packages'
    | 'coupons'
    | 'fleets'
    | 'express-details';
  content: any;
  selected?: boolean;
}

@Component({
  selector: 'app-dark-express-wash-packages-preview',
  standalone: true,
  providers: [NotificationService],
  imports: [
    CommonModule,
    PageHeaderComponent,
    PageFooterComponent,
    SharedPackageSelectionModalComponent,
  ],
  templateUrl: './express-wash-packages-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./express-wash-packages-preview.component.scss'],
})
export class DarkExpressWashPackagesPreviewComponent implements OnInit, OnChanges {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {};
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  packages: Package[] = [];
  loading = false;
  error: string | null = null;
  readonly shimmerSlots = [0, 1, 2] as const;

  // Track selected tab for each package (packageId -> 'single' | 'unlimited')
  selectedTabs = new Map<string, 'single' | 'unlimited'>();

  // Page data from JSON
  headerData: any = null;
  packageSectionData: any = null;
  footerData: any = null;
  pageTitleData: any = null;

  // Package Selection Modal
  showPackageSelectionModal = false;
  selectedPackageForModal: SelectedPackage | null = null;

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadPageContent();
  }

  /**
   * Handle Angular OnChanges lifecycle updates: reload page content, update header navigation, and refresh package/title section data when relevant inputs change.
   * @example
   * gOnChanges({ pageData: { firstChange: false }, elements: { firstChange: false }, pages: {}, currentPageType: {} })
   * void
   * @param {{SimpleChanges}} {{changes}} - The SimpleChanges object containing previous and current values for bound inputs.
   * @returns {{void}} No return value; triggers state updates and side effects (loading content, updating navigation, and refreshing section data).
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
    // Reload page content when elements change (for background color updates)
    if (changes['elements'] && !changes['elements'].firstChange) {
      // Force reload of packageSectionData to get updated background color
      const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
      const sections = page?.sections || {};
      this.packageSectionData = sections.package_section || {};
      this.pageTitleData = sections['page-title'] || sections.hero_section || {};
    }
  }

  /**
   * Load and parse page content from this.pageData into the component's section properties, then update header navigation and load packages.
   * @example
   * this.loadPageContent()
   * undefined
   * @returns {{void}} No return value.
   **/
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('?? Loading Express Wash Packages page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page.sections
    // Preview format: pageData.json_data.page.sections
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || this.pageData?.sections || {};

    this.headerData = sections.header || {};
    this.packageSectionData = sections.package_section || {};
    this.footerData = sections.footer || {};
    // Load page-title section (for heading and description)
    this.pageTitleData = sections['page-title'] || sections.hero_section || {};

    this.logger.debug('Header data:', this.headerData);
    this.logger.debug('Package section data:', this.packageSectionData);
    this.logger.debug('Footer data:', this.footerData);
    this.logger.debug('Page title data:', this.pageTitleData);

    // Update header navigation to match current pages
    this.updateHeaderNavigation();

    // Load packages from API
    this.loadPackages();
  }

  /**
   * Update the header navigation and CTA button based on available pages and current page type.
   * @example
   * updateHeaderNavigation()
   * undefined
   * @param {{void}} {{none}} - No arguments.
   * @returns {{void}} Updates this.headerData.navigation and this.headerData.ctaButton in place and returns nothing.
   **/
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug(
        'Updated header navigation for Express Wash Packages:',
        this.headerData.navigation
      );
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Loads car wash packages from the backend, initializes default selected tabs for each package, and manages loading/error state.
   * @example
   * loadPackages()
   * undefined
   * @param {{void}} {{none}} - No parameters.
   * @returns {{void}} No return value; updates component state (packages, selectedTabs, loading, error).
   **/
  private loadPackages(): void {
    this.loading = true;
    this.error = null;

    this.packageService.getCarWashPackages().subscribe({
      next: response => {
        this.logger.debug('? Packages loaded:', response);
        if (response.status && response.data) {
          this.packages = response.data;
          this.logger.debug('Packages count:', this.packages.length);

          // Initialize selected tabs - default to 'single' if available, otherwise 'unlimited'
          this.packages.forEach(pkg => {
            if (this.hasSingleWash(pkg)) {
              this.selectedTabs.set(pkg.id, 'single');
            } else if (this.hasUnlimitedWash(pkg)) {
              this.selectedTabs.set(pkg.id, 'unlimited');
            }
          });
        } else {
          this.error = 'No packages available';
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('? Error loading packages:', err);
        this.error = 'Failed to load packages';
        this.loading = false;
      },
    });
  }

  // Format price for display
  formatPrice(price: number | string | undefined): string {
    if (!price) return '$0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  }

  // Get package image or fallback
  getPackageImage(pkg: Package): string {
    // Use image from API if available, otherwise fall back to static placeholder
    if (pkg.image && pkg.image.trim() !== '') {
      return pkg.image;
    }
    return PACKAGE_ICON_FALLBACK;
  }

  // Get package image fit/scaletype
  /**
   * Determine the CSS object-fit value to use for a package image based on the package's scaletype configuration.
   * @example
   * etPackageImageFit({ id: 'basic' })
   * 'cover'
   * @param {{Package}} {{pkg}} - Package object whose scaletype is looked up in the packageSectionData packages array.
   * @returns {{string}} CSS object-fit value ('fill', 'cover', 'contain', 'scale-down'), defaults to 'cover'.
   **/
  getPackageImageFit(pkg: Package): string {
    // Check package_section.packages array for this package's scaletype
    const packageSectionPackages = this.packageSectionData?.packages || [];
    const packageConfig = packageSectionPackages.find((p: any) => p.id === pkg.id);

    if (packageConfig?.scaletype) {
      // Map API scaletype to CSS object-fit values
      const scaletype = packageConfig.scaletype;
      if (scaletype === 'fill' || scaletype === 'stretch') {
        return 'fill';
      } else if (scaletype === 'cover') {
        return 'cover';
      } else if (scaletype === 'contain') {
        return 'contain';
      } else if (scaletype === 'fit') {
        return 'scale-down';
      }
    }

    // Default to cover if not specified
    return 'cover';
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = PACKAGE_ICON_FALLBACK;
  }

  // Check if package has single wash type
  hasSingleWash(pkg: Package): boolean {
    return !!(
      pkg.wash_types &&
      pkg.wash_types.includes('single_wash') &&
      pkg.single_list_price &&
      parseFloat(pkg.single_list_price) > 0
    );
  }

  // Check if package has unlimited/monthly wash type
  hasUnlimitedWash(pkg: Package): boolean {
    return !!(
      pkg.wash_types &&
      pkg.wash_types.includes('monthly_membership') &&
      pkg.monthly_list_price &&
      parseFloat(pkg.monthly_list_price) > 0
    );
  }

  // Get selected tab for a package
  getSelectedTab(pkg: Package): 'single' | 'unlimited' {
    const selected = this.selectedTabs.get(pkg.id);
    if (selected) return selected;
    // Default to single if available, otherwise unlimited
    return this.hasSingleWash(pkg) ? 'single' : 'unlimited';
  }

  // Set selected tab for a package
  setSelectedTab(pkg: Package, tab: 'single' | 'unlimited'): void {
    this.selectedTabs.set(pkg.id, tab);
    this.cdr.detectChanges(); // Force change detection to update prices
  }

  // Get display price based on selected tab
  getListPrice(pkg: Package): string {
    const tab = this.getSelectedTab(pkg);
    return tab === 'unlimited' ? pkg.monthly_list_price : pkg.single_list_price;
  }

  // Get strikeoff price based on selected tab
  getStrikeoffPrice(pkg: Package): string {
    const tab = this.getSelectedTab(pkg);
    return tab === 'unlimited' ? pkg.monthly_strikeoff_price : pkg.single_strikeoff_price;
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[sectionKey] !== false;
  }

  /**
   * Get package section element for selection
   */
  getPackageSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'packages' ||
        el.id === 'packages' ||
        (el as any).originalKey === 'package_section'
    );
  }

  /**
   * Get package section background color
   */
  getPackageSectionBackgroundColor(): string | null {
    // Priority 1: Check element content (most up-to-date, updated immediately)
    const packageElement = this.getPackageSectionElement();
    const packageContent = packageElement?.content as any;
    if (packageContent?.backgroundColor) {
      return packageContent.backgroundColor;
    }
    // Priority 2: Check packageSectionData (from API/pageData)
    if (this.packageSectionData?.backgroundColor) {
      return this.packageSectionData.backgroundColor;
    }
    // Priority 3: Check page-level theme background color
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.theme?.background_color) {
      return page.theme.background_color;
    }
    // Priority 4: Check pageData directly
    const sections = page?.sections || {};
    const packageSection = sections.package_section || {};
    if (packageSection.backgroundColor) {
      return packageSection.backgroundColor;
    }
    // Default fallback
    return null;
  }

  /**
   * Get page title section background color (same as package section)
   */
  getPageTitleBackgroundColor(): string | null {
    // Use the same background color as the package section
    return this.getPackageSectionBackgroundColor();
  }

  /**
   * Get page title text
   */
  getPageTitle(): string {
    if (this.pageTitleData?.title) {
      if (typeof this.pageTitleData.title === 'string') {
        return this.pageTitleData.title;
      } else if (this.pageTitleData.title?.label) {
        return this.pageTitleData.title.label;
      }
    }
    return 'Express Wash Packages';
  }

  /**
   * Get page subtitle/description text
   */
  getPageSubtitle(): string {
    if (this.pageTitleData?.description) {
      return this.pageTitleData.description;
    } else if (this.pageTitleData?.subtitle) {
      return this.pageTitleData.subtitle;
    }
    return 'Choose from our range of washes that suit all needs and budgets.';
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    if (!this.pageTitleData?.title) return {};

    const title = this.pageTitleData.title;
    const titleStyleObj = title.style?.text_style;

    if (!titleStyleObj || typeof titleStyleObj !== 'object') return {};

    const styles: any = {};
    if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
    if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
    if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
    if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;

    return styles;
  }

  /**
   * Handle package section click
   */
  onPackageSectionClick(event: Event): void {
    // Stop event propagation to prevent conflicts
    event.stopPropagation();

    const packageElement: WebsiteElement = {
      id: 'packages',
      type: 'packages',
      content: {
        ...(this.packageSectionData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        pageTitleData: this.pageTitleData,
        backgroundColor: this.getPackageSectionBackgroundColor(),
      },
      selected: true,
      originalKey: 'package_section',
    } as any;

    this.logger.debug('📦 Package section clicked, emitting element:', packageElement);
    this.elementSelect.emit(packageElement);
  }

  /**
   * Retrieve the header logo object from the component's header content, falling back to defaults if not present.
   * @example
   * etHeaderLogo()
   * { image: 'https://example.com/logo.png', text: 'Express Wash', link: '/' }
   * @returns {Logo} Logo object with properties image, text and link (defaults: text 'Logo', link '/').
   */
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.logo) {
      return {
        image: headerContent.logo.image || headerContent.logo.url || '',
        text: headerContent.logo.text || 'Logo',
        link: headerContent.logo.link || '/',
      };
    }
    return { text: 'Logo', link: '/' };
  }

  /**
   * Returns the header navigation items, using header.content.navigation if available or generating them from visible pages as a fallback.
   * @example
   * etHeaderNavigation()
   * [{ label: "Home", link: "#home", active: true }]
   * @returns {{NavigationItem[]}} The array of navigation items for the header.
   */
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      return headerContent.navigation;
    }
    // Fallback to generating from pages
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Retrieve the CTA button configuration from the header content, using header-provided values when available and falling back to sensible defaults.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @returns {{CTAButton | null}} CTA button object containing text, link, backgroundColor, and textColor, or null if unavailable.
   **/
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.ctaButton) {
      return {
        text: headerContent.ctaButton.text || headerContent.ctaButton.label || 'Book Wash',
        link: headerContent.ctaButton.link || '#',
        backgroundColor: headerContent.ctaButton.backgroundColor || '#1B5BFF',
        textColor: headerContent.ctaButton.textColor || '#FFFFFF',
      };
    }
    return {
      text: 'Book Wash',
      link: '#',
      backgroundColor: '#1B5BFF',
      textColor: '#FFFFFF',
    };
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.backgroundColor) {
      return headerContent.backgroundColor as string;
    }
    return null;
  }

  /**
   * Get footer quick links from active pages
   * Filters out pages where visible === false (active === false)
   */
  getFooterQuickLinks(): { label: string; link: string }[] {
    // First, try to get from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Retrieve normalized social links from the footer content as an array of objects with platform, url and icon.
   * @example
   * etFooterSocialLinks()
   * // => [{ platform: "Facebook", url: "https://facebook.com/yourpage", icon: "facebook" }]
   * @returns {Array<{ platform: string; url: string; icon: string }>} Array of social link objects.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }
    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.socialLinksTitle || 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.quickLinksTitle || 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.copyright) {
      return footerContent.copyright;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Retrieve the footer logo configuration (text and image) with fallback to header logo and defaults.
   * @example
   * etFooterLogo()
   * { text: 'way', image: '' }
   * @returns {{ {text?: string; image?: string} | undefined }} Footer logo object with optional text and image properties, or undefined if not available.
   **/
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#1B1B1B';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  onHeaderClick(): void {
    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#FFFFFF';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#D3D5D9';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Plus Jakarta Sans';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  onBuyNowClick(pkg: Package, event: Event): void {
    event.stopPropagation();
    this.selectedPackageForModal = pkg;
    this.showPackageSelectionModal = true;
  }

  getPackagesForModal(): Package[] {
    if (this.selectedPackageForModal) {
      return [this.selectedPackageForModal as Package];
    }
    return this.packages;
  }

  /**
   * Close package selection modal
   */
  onClosePackageSelectionModal(): void {
    this.showPackageSelectionModal = false;
    this.selectedPackageForModal = null;
  }

  /**
   * Handle package selection from modal
   */
  onPackageSelected(selection: SelectedPackage): void {
    this.selectedPackageForModal = selection;
    this.logger.debug('📦 Package selected:', selection);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
